import React from 'react';
// @ts-ignore
import { submitPteCoreSpeakingAttempt } from '../../api';

// --- Existing Scoring Logic ---

// Interfaces aligned with backend expectations
export interface ScoreResult {
    content: number;
    pronunciation: number;
    fluency: number;
    overall: number;
    feedback: string;
    transcription: string;
    detailedAnalysis?: {
        word: string;
        status: 'correct' | 'omission' | 'insertion' | 'substitution';
    }[];
}

export const scoreReadAloudAttempt = async (
    audioBlob: Blob,
    referenceText: string,
    taskId: string,
    transcription: string
): Promise<ScoreResult> => { // ... existing implementation ...
    try {
        const formData = new FormData();
        formData.append('audio', audioBlob, 'attempt.wav');
        formData.append('taskId', taskId);
        formData.append('transcription', transcription);

        const user = JSON.parse(localStorage.getItem('user_info') || '{}');
        if (user._id) {
            formData.append('userId', user._id);
        }

        const response = await submitPteCoreSpeakingAttempt(formData);

        if (response.data && response.data.score) {
            return response.data.score as ScoreResult;
        }

        throw new Error("Invalid response structure from server");
    } catch (error) {
        console.error("Scoring failed:", error);
        return {
            content: 0,
            pronunciation: 0,
            fluency: 0,
            overall: 0,
            feedback: "Scoring service unavailable or failed.",
            transcription: transcription || "(Audio not processed)",
        };
    }
};
export const scoreRepeatSentenceAttempt = async (
    audioBlob: Blob,
    referenceText: string,
    taskId: string,
    transcription: string
): Promise<ScoreResult> => {
    try {
        const formData = new FormData();
        formData.append('audio', audioBlob, 'attempt.wav');
        formData.append('taskId', taskId);
        formData.append('transcription', transcription);

        const user = JSON.parse(localStorage.getItem('user_info') || '{}');
        if (user._id) {
            formData.append('userId', user._id);
        }

        const response = await submitPteCoreSpeakingAttempt(formData);

        if (response.data && response.data.score) {
            return response.data.score as ScoreResult;
        }

        throw new Error("Invalid response structure from server");
    } catch (error) {
        console.error("Scoring failed:", error);
        return {
            content: 0,
            pronunciation: 0,
            fluency: 0,
            overall: 0,
            feedback: "Scoring service unavailable or failed.",
            transcription: transcription || "(Audio not processed)",
        };
    }
};
export const scoreDescribeImageAttempt = async (
    audioBlob: Blob,
    imageDescription: string,
    taskId: string,
    transcription: string
): Promise<ScoreResult> => {
    try {
        const formData = new FormData();
        formData.append('audio', audioBlob, 'attempt.wav');
        formData.append('taskId', taskId);
        formData.append('transcription', transcription);

        const user = JSON.parse(localStorage.getItem('user_info') || '{}');
        if (user._id) {
            formData.append('userId', user._id);
        }

        const response = await submitPteCoreSpeakingAttempt(formData);

        if (response.data && response.data.score) {
            return response.data.score as ScoreResult;
        }

        throw new Error("Invalid response structure from server");
    } catch (error) {
        console.error("Scoring failed:", error);
        return {
            content: 0,
            pronunciation: 0,
            fluency: 0,
            overall: 0,
            feedback: "Scoring service unavailable or failed.",
            transcription: transcription || "(Audio not processed)",
        };
    }
};
export const scoreRespondToSituationAttempt = async (
    audioBlob: Blob,
    situationText: string,
    taskId: string,
    transcription: string
): Promise<ScoreResult> => {
    try {
        const formData = new FormData();
        formData.append('audio', audioBlob, 'attempt.wav');
        formData.append('taskId', taskId);
        formData.append('transcription', transcription);

        const user = JSON.parse(localStorage.getItem('user_info') || '{}');
        if (user._id) {
            formData.append('userId', user._id);
        }

        const response = await submitPteCoreSpeakingAttempt(formData);

        if (response.data && response.data.score) {
            return response.data.score as ScoreResult;
        }

        throw new Error("Invalid response structure from server");
    } catch (error) {
        console.error("Scoring failed:", error);
        return {
            content: 0,
            pronunciation: 0,
            fluency: 0,
            overall: 0,
            feedback: "Scoring service unavailable or failed.",
            transcription: transcription || "(Audio not processed)",
        };
    }
};
export const scoreAnswerShortQuestionAttempt = async (
    audioBlob: Blob,
    questionText: string,
    taskId: string,
    transcription: string
): Promise<ScoreResult> => {
    try {
        const formData = new FormData();
        formData.append('audio', audioBlob, 'attempt.wav');
        formData.append('taskId', taskId);
        formData.append('transcription', transcription);

        const user = JSON.parse(localStorage.getItem('user_info') || '{}');
        if (user._id) {
            formData.append('userId', user._id);
        }

        const response = await submitPteCoreSpeakingAttempt(formData);

        if (response.data && response.data.score) {
            return response.data.score as ScoreResult;
        }

        throw new Error("Invalid response structure from server");
    } catch (error) {
        console.error("Scoring failed:", error);
        return {
            content: 0,
            pronunciation: 0,
            fluency: 0,
            overall: 0,
            feedback: "Scoring service unavailable or failed.",
            transcription: transcription || "(Audio not processed)",
        };
    }
};


// --- New UI Component ---

export interface SpeakingResult {
    taskId: string;
    taskTitle: string;
    transcription: string;
    score: ScoreResult;
}

interface SpeakingScoreResultProps {
    results: SpeakingResult[];
    onRetry: () => void;
    onNext?: () => void;
    nextLabel?: string;
}

// CLB bands for PTE Core Speaking based on official conversion tables
const getSpeakingCLB = (score: number): number => {
    if (score >= 89) return 10;
    if (score >= 84) return 9;
    if (score >= 76) return 8;
    if (score >= 68) return 7;
    if (score >= 59) return 6;
    if (score >= 51) return 5;
    if (score >= 42) return 4;
    if (score >= 34) return 3;
    return 3;
};

const TraitBar: React.FC<{ label: string; score: number; maxScore: number; comments?: string }> = ({ label, score, maxScore, comments }) => {
    const percentage = (score / maxScore) * 100;

    let colorClass = 'bg-green-500';
    if (percentage < 50) colorClass = 'bg-red-500';
    else if (percentage < 80) colorClass = 'bg-yellow-500';

    return (
        <div className="mb-3">
            <div className="flex justify-between items-end mb-1">
                <span className="font-medium text-gray-700 capitalize text-sm">{label.replace(/([A-Z])/g, ' $1').trim()}</span>
                <span className="text-sm font-bold text-gray-900">
                    {score} / {maxScore}
                </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                    className={`h-2 rounded-full transition-all duration-500 ${colorClass}`}
                    style={{ width: `${percentage}%` }}
                ></div>
            </div>
            {comments && (
                <p className="text-xs text-gray-500 italic mt-1">{comments}</p>
            )}
        </div>
    );
};

const SpeakingScoreResult: React.FC<SpeakingScoreResultProps> = ({ results, onRetry, onNext, nextLabel }) => {
    // Calculate overall score out of 90.
    // If we receive multiple results, we average their overall scores.
    const totalScore = results.reduce((sum, r) => sum + r.score.overall, 0);
    const scoreOut90 = results.length > 0 ? Math.round(totalScore / results.length) : 0;
    const clbLevel = getSpeakingCLB(scoreOut90);

    return (
        <div className="ptest-root">
            {/* Overall Score Header */}
            <div className="bg-white rounded-xl shadow-lg p-8 mb-8 border border-gray-200">
                <h2 className="text-2xl font-bold text-center text-gray-800 mb-6">Speaking Test Results</h2>

                <div className="flex flex-wrap justify-center gap-8">
                    {/* Score out of 90 */}
                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 px-8 py-6 rounded-xl border border-blue-200 text-center">
                        <span className="block text-sm text-blue-600 font-semibold uppercase tracking-wider mb-2">Overall Score</span>
                        <span className="block text-5xl font-extrabold text-blue-900">
                            {scoreOut90} <span className="text-2xl text-blue-400">/ 90</span>
                        </span>
                    </div>

                    {/* CLB Band */}
                    <div className="bg-gradient-to-br from-amber-50 to-amber-100 px-8 py-6 rounded-xl border border-amber-200 text-center">
                        <span className="block text-sm text-amber-700 font-semibold uppercase tracking-wider mb-2">CLB Level</span>
                        <span className="block text-5xl font-extrabold text-amber-900">
                            {clbLevel}
                        </span>
                    </div>
                </div>

                {/* CLB Reference Table */}
                <div className="mt-8 overflow-x-auto">
                    <table className="mx-auto text-sm border-collapse">
                        <thead>
                            <tr className="bg-amber-500 text-white">
                                <th className="px-4 py-2 border border-amber-600">CLB</th>
                                <th className="px-4 py-2 border border-amber-600">PTE Core Speaking</th>
                            </tr>
                        </thead>
                        <tbody>
                            {[
                                { clb: 10, range: '89-90' },
                                { clb: 9, range: '84-88' },
                                { clb: 8, range: '76-83' },
                                { clb: 7, range: '68-75' },
                                { clb: 6, range: '59-67' },
                                { clb: 5, range: '51-58' },
                                { clb: 4, range: '42-50' },
                                { clb: 3, range: '34-41' },
                            ].map((row) => (
                                <tr
                                    key={row.clb}
                                    className={clbLevel === row.clb ? 'bg-amber-100 font-bold' : 'bg-white'}
                                >
                                    <td className="px-4 py-2 border border-gray-300 text-center">{row.clb}</td>
                                    <td className="px-4 py-2 border border-gray-300 text-center">{row.range}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Individual Task Feedback */}
            <h3 className="text-xl font-bold text-gray-800 mb-4">Detailed Feedback by Question</h3>

            {results.map((result, index) => {
                const title = result.taskTitle?.toLowerCase() || "";
                let contentMax = 5;
                let contentLabel = "Content";

                if (title.includes('read aloud')) contentMax = 3;
                if (title.includes('repeat sentence')) contentMax = 3;
                if (title.includes('answer short question')) {
                    contentMax = 1;
                    contentLabel = "Vocabulary";
                }
                if (title.includes('respond to a situation')) {
                    contentLabel = "Appropriacy";
                }

                return (
                    <div key={result.taskId} className="bg-white rounded-xl shadow-md p-6 mb-6 border border-gray-200">
                        <div className="flex justify-between items-center mb-4 pb-4 border-b">
                            <h4 className="text-lg font-bold text-gray-800">
                                Question {index + 1}: {result.taskTitle}
                            </h4>
                            <div className="bg-blue-100 px-4 py-2 rounded-lg">
                                <span className="font-bold text-blue-800">
                                    {result.score.overall.toFixed(0)} / 90
                                </span>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
                            <TraitBar label={contentLabel} score={result.score.content} maxScore={contentMax} />
                            <TraitBar label="Pronunciation" score={result.score.pronunciation} maxScore={5} />
                            <TraitBar label="Fluency" score={result.score.fluency} maxScore={5} />
                        </div>

                        <div className="space-y-4 mt-6">
                            <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
                                <h4 className="text-sm font-bold text-blue-900 mb-2">AI Feedback</h4>
                                <p className="text-sm text-blue-800 leading-relaxed">{result.score.feedback}</p>
                            </div>

                            {/* Show user's transcription */}
                            {result.transcription && (
                                <div className="mt-4 pt-4 border-t">
                                    <h5 className="font-semibold text-gray-700 mb-2">Transcribed Response:</h5>
                                    <div className="bg-gray-50 p-4 rounded-lg text-gray-700 text-sm italic border">
                                        {result.transcription}
                                    </div>
                                </div>
                            )}

                            {/* Detailed Analysis */}
                            {result.score.detailedAnalysis && result.score.detailedAnalysis.length > 0 && (
                                <div className="mt-4">
                                    <h5 className="font-semibold text-gray-700 mb-2">Detailed Word Analysis:</h5>
                                    <div className="flex flex-wrap gap-2 bg-gray-50 p-4 rounded-lg border">
                                        {result.score.detailedAnalysis.map((item, i) => {
                                            let color = 'text-gray-800';
                                            if (item.status === 'omission') color = 'text-red-500 line-through';
                                            else if (item.status === 'insertion') color = 'text-yellow-600 underline';
                                            else if (item.status === 'substitution') color = 'text-orange-500';
                                            else if (item.status === 'correct') color = 'text-green-600';

                                            if (!item.word) return null;
                                            return (
                                                <span key={i} className={color} title={item.status}>
                                                    {item.word}
                                                </span>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                );
            })}

            {/* Action Buttons */}
            <div className="ptest-header" style={{ marginTop: '20px' }}>
                <div></div>
                <div className="right-controls flex gap-4">
                    <button
                        onClick={onRetry}
                        className="btn btn-primary"
                    >
                        Try Again
                    </button>
                    {onNext && (
                        <button
                            onClick={onNext}
                            className="btn btn-primary bg-green-600 hover:bg-green-700 border-green-600"
                        >
                            {nextLabel || 'Next Section'}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SpeakingScoreResult;
